import { getInjectedEnabledLanguages } from "./runtime-branding"

/** Every interface language the app ships, in switcher order. */
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "sv", label: "Svenska" },
  { code: "no", label: "Norsk" },
  { code: "fi", label: "Suomi" },
  { code: "de", label: "Deutsch" },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]["code"]

const PREFERRED_FALLBACK: LanguageCode = "sv"

function isLanguageCode(code: string): code is LanguageCode {
  return SUPPORTED_LANGUAGES.some((language) => language.code === code)
}

/**
 * The languages this deployment offers (#3226): the admin's list when one is
 * stored, otherwise all of them. Unknown codes are dropped, and a list that
 * drops to nothing means "all": an interface with no language cannot render.
 */
export function enabledLanguageCodes(): LanguageCode[] {
  const stored = (getInjectedEnabledLanguages() ?? []).filter(isLanguageCode)
  const unique = [...new Set(stored)]
  return unique.length > 0 ? unique : SUPPORTED_LANGUAGES.map((language) => language.code)
}

/** Swedish when it is offered, otherwise the first offered language. */
export function fallbackLanguage(enabled: LanguageCode[]): LanguageCode {
  return enabled.includes(PREFERRED_FALLBACK) ? PREFERRED_FALLBACK : enabled[0]
}

/**
 * Swedish is always loaded, offered or not: the tender chat context is built
 * with `i18next.getFixedT("sv")` so the model always reads Swedish, and
 * i18next resolves a fixed language only if it is supported and bundled.
 */
const ALWAYS_LOADED: LanguageCode[] = ["sv"]

/** The languages i18next loads: the offered ones plus Swedish. */
export function loadedLanguageCodes(enabled: LanguageCode[]): LanguageCode[] {
  return [...new Set([...enabled, ...ALWAYS_LOADED])]
}

/** The translation bundles for `codes`. */
export function resourcesFor<T>(
  all: Record<LanguageCode, T>,
  codes: LanguageCode[]
): Partial<Record<LanguageCode, T>> {
  return Object.fromEntries(codes.map((code) => [code, all[code]]))
}

/**
 * Maps what the browser detector found (a saved choice, a cookie, the browser
 * language) onto an offered language. i18next keeps a detected language as
 * `i18n.language` whenever its bundle is loaded, so without this a saved
 * Swedish choice would bring back a Swedish interface on a deployment that
 * does not offer Swedish, just because the bundle is there for the tender
 * context. Everything keyed on `i18n.language` (the header info text, the
 * consent text, the switcher) would follow it.
 */
export function toOfferedLanguage(detected: string, enabled: LanguageCode[]): string {
  const base = detected.split(/[-_]/)[0].toLowerCase()
  return (enabled as string[]).includes(base) ? detected : fallbackLanguage(enabled)
}
